import type { LiveDrawing, LiveNote, LiveScene, LiveToken } from "../types/generated/live";

// What the Exportar imagem picture is made of, with no renderer in it, so the dialog can ask before the renderer has loaded.

// What a render rejects with when `stale()` says its picture is no longer wanted; the dialog ignores it.
export class ExportCancelled extends Error {
	constructor() {
		super("Export cancelled");
		this.name = "ExportCancelled";
	}
}

// The most pixels a picture has on any device: an iPhone drew 67 MP in pieces and lost the page at 33.6 MP on one stage the size of the
// picture, so the cap leaves room for older phones (docs/architecture/maps.md).
export const EXPORT_CAP = 16_777_216;
// The side of the stage the picture is drawn through, in output pixels.
export const EXPORT_TILE = 2048;
// Pieces of kinds other than these stay in a picture drawn without the pieces: the Objetos are part of the map.
const PIECES = new Set<LiveToken["kind"]>(["sheet", "monster", "marker"]);

type Size = Pick<LiveScene, "width_px" | "height_px">;
type Collection<T> = T[] | Record<string, T> | undefined;
export type ExportSnapshot = { scene: Size; tokens?: Collection<LiveToken>; drawings?: Collection<LiveDrawing>; notes?: Collection<LiveNote> } & Record<
	string,
	unknown
>;
// The picture's pixels for a scale; the same rounding the cap is checked against.
export function outputSize(scene: Size, scale: number) {
	return { width: Math.max(1, Math.round(scene.width_px * scale)), height: Math.max(1, Math.round(scene.height_px * scale)) };
}

// The scene at its own pixels, scaled down uniformly only as far as it takes to fit the cap, never enlarged.
export function exportSize(scene: Size, cap = EXPORT_CAP) {
	let scale = Math.min(1, Math.sqrt(cap / (scene.width_px * scene.height_px)));
	let size = outputSize(scene, scale);
	while (size.width * size.height > cap) {
		scale *= 0.9999;
		size = outputSize(scene, scale);
	}
	return { ...size, scale };
}

// The rectangles that cover the picture row by row, the last row and column narrower.
export function exportTiles(width: number, height: number, tile = EXPORT_TILE) {
	const tiles: { x: number; y: number; width: number; height: number }[] = [];
	for (let y = 0; y < height; y += tile) {
		for (let x = 0; x < width; x += tile) tiles.push({ x, y, width: Math.min(tile, width - x), height: Math.min(tile, height - y) });
	}
	return tiles;
}

export function exportFileName(name: string, master: boolean) {
	const slug =
		name
			.normalize("NFD")
			.replace(/\p{M}/gu, "")
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-+|-+$/g, "") || "mapa";
	return `${slug}${master ? "-mestre" : ""}.png`;
}

const listOf = <T>(collection: Collection<T>): T[] => (Array.isArray(collection) ? collection : Object.values(collection ?? {}));

// A copy fit to draw: no health bar ever, and without the pieces none of the kinds in PIECES. The input is the board's state or the server's answer, whose collections are keyed objects or arrays.
export function exportSnapshot<T extends ExportSnapshot>(snapshot: T, { pieces }: { pieces: boolean }) {
	const tokens = listOf(snapshot.tokens)
		.filter((token) => pieces || !PIECES.has(token.kind))
		.map((token) => {
			const copy: Partial<LiveToken> & { health?: number } = { ...token };
			delete copy.pv;
			delete copy.max_pv;
			delete copy.health;
			return copy as LiveToken;
		});
	return { ...snapshot, tokens, drawings: listOf(snapshot.drawings), notes: listOf(snapshot.notes) };
}
